import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from 'lucide-react';
import { Brand } from '@/types/firestore';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectBrandById,
  selectGlobalDataInitialized,
  selectGlobalDataLoading,
  selectGlobalDataError,
} from '@/store/slices/globalDataSlice';
import { selectSelectedEntity, selectEditorOperationLoading } from '@/store/slices/editorSlice';

interface BrandEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (brand: Partial<Brand>) => Promise<void>;
}

export const BrandEditDialog: React.FC<BrandEditDialogProps> = ({
  open,
  onOpenChange,
  onSave
}) => {
  const dispatch = useAppDispatch();
  const selectedEntity = useAppSelector(selectSelectedEntity);
  const isSaveLoading = useAppSelector(selectEditorOperationLoading) === 'pending';

  const brandFromStore = useAppSelector(state =>
    selectedEntity?.type === 'brand' && selectedEntity.id
      ? selectBrandById(state, selectedEntity.id)
      : null
  );
  const globalDataInitialized = useAppSelector(selectGlobalDataInitialized);
  const globalDataLoading = useAppSelector(selectGlobalDataLoading);
  const globalDataError = useAppSelector(selectGlobalDataError);
  
  const [editingBrand, setEditingBrand] = useState<Partial<Brand> | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [displayError, setDisplayError] = useState<string | null>(null);

  useEffect(() => {
    const entityId = selectedEntity?.id;
    if (open && selectedEntity?.type === 'brand' && entityId) {
      if (brandFromStore) {
        setEditingBrand(brandFromStore);
        setIsLoadingDetails(false);
        setDisplayError(null);
      } else if (globalDataInitialized) {
        const fetchDirect = async () => {
          setIsLoadingDetails(true);
          setDisplayError(null);
          setEditingBrand(null);
          try {
            const brandDocRef = doc(db, 'brands', entityId);
            const docSnap = await getDoc(brandDocRef);
            if (docSnap.exists()) {
              const fetchedData = { ...docSnap.data(), id: docSnap.id } as Brand;
              setEditingBrand(fetchedData);
            } else {
              setDisplayError("Brand not found.");
            }
          } catch (error: any) {
            console.error("Error fetching brand details directly:", error);
            setDisplayError(error.message || "Failed to load brand details.");
          } finally {
            setIsLoadingDetails(false);
          }
        };
        fetchDirect();
      } else if (globalDataLoading === 'pending') {
        setIsLoadingDetails(true);
        setDisplayError(null);
        setEditingBrand(null);
      } else if (globalDataError) {
        setDisplayError(`Global data error: ${globalDataError}`);
        setIsLoadingDetails(false);
        setEditingBrand(null);
      }
    } else if (!open) {
      setEditingBrand(null);
      setDisplayError(null);
      setIsLoadingDetails(false);
    }
  }, [
    open, 
    selectedEntity, 
    brandFromStore, 
    globalDataInitialized, 
    globalDataLoading, 
    globalDataError,
    dispatch
  ]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        onOpenChange(false);
      }
    };
    if (open) {
      window.addEventListener('keydown', handleEscapeKey);
    }
    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [open, onOpenChange]);

  const handleFieldChange = (field: keyof Brand, value: any) => {
    setEditingBrand((prev) => prev ? { ...prev, [field]: value } : null);
  };

  const handleSave = async () => {
    if (!editingBrand || isSaveLoading) return;
    try {
      await onSave(editingBrand as Brand);
    } catch (error) {
      console.error('Error saving brand:', error);
    }
  };

  const handleOpenChangeInternal = (newOpenState: boolean) => {
    if (!newOpenState) {
      setEditingBrand(null);
      setDisplayError(null);
      setIsLoadingDetails(false);
    }
    onOpenChange(newOpenState);
  };

  const handleCancel = () => {
    handleOpenChangeInternal(false);
  };

  const derivedIsLoading = isLoadingDetails || (globalDataLoading === 'pending' && !brandFromStore && !globalDataInitialized);
  const derivedError = displayError || (globalDataInitialized && !brandFromStore && !isLoadingDetails && !globalDataError ? "Brand not found." : null);

  if (!open) return null;

  if (derivedIsLoading) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChangeInternal}>
        <DialogContent className="sm:max-w-[500px] flex items-center justify-center">
          <DialogHeader>
            <DialogTitle>Loading Data</DialogTitle>
            <DialogDescription>
              Please wait while the brand details are being loaded.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-4">
            <Loader2 className="mr-2 h-8 w-8 animate-spin" />
            <span>Loading brand details...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (derivedError) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChangeInternal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
            <DialogDescription>{derivedError}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  
  if (!editingBrand && selectedEntity?.type === 'brand' && !derivedIsLoading && !derivedError) {
     return (
      <Dialog open={open} onOpenChange={handleOpenChangeInternal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
            <DialogDescription>Could not load brand data to edit.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (!editingBrand) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChangeInternal}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Brand</DialogTitle>
          <DialogDescription>
            Make changes to the brand information below.
            {derivedIsLoading && <span className="text-xs text-muted-foreground ml-2">(Updating...)</span>}
          </DialogDescription>
        </DialogHeader>
        
        <div className={`grid gap-4 py-4 ${isSaveLoading || derivedIsLoading ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="name" className="text-right">Name</Label>
            <Input 
              id="name" 
              value={editingBrand.name || ''} 
              onChange={(e) => handleFieldChange('name', e.target.value)}
              className="col-span-3"
              disabled={isSaveLoading}
            />
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="brandSize" className="text-right">Brand Size</Label>
            <Select
              value={editingBrand.brandSize || 'unknown'}
              onValueChange={(value: Brand['brandSize']) => handleFieldChange('brandSize', value)}
              disabled={isSaveLoading}
            >
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder="Select brand size" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="craft">Craft</SelectItem>
                <SelectItem value="mass">Mass Market</SelectItem>
                <SelectItem value="unknown">Unknown</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" onClick={handleCancel} disabled={isSaveLoading}>Cancel</Button>
          </DialogClose>
          <Button onClick={handleSave} disabled={isSaveLoading || derivedIsLoading}>
            {isSaveLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 